import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { merge, sortBy } from 'lodash';
import { TeamEdit, TeamRole } from '../model/team';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { UserService } from 'src/app/core/service/user.service';
import { TeamService } from 'src/app/core/service/team.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { Code } from 'src/app/core/model/dictionary';
import { SettingService } from 'src/app/core/service/setting.service';

@Component({
  selector: 'app-team-edit',
  templateUrl: './team-edit.component.html',
  styleUrls: ['./team-edit.component.scss']
})
export class TeamEditComponent implements OnInit
{
  @Input() public isNew = false;

  @Input() public memberId: number;
  @Input() public caseId: number;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<TeamEdit> = new EventEmitter();
  @Output() public delete: EventEmitter<TeamEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public displaySubRoles = false;
  public patientLabel = {};

  /**
   * Maximum number of items for virtualization. This is to accelerate the DOM performance.
   */
  public virtualSubRole: any = {
    itemHeight: 30
  };

  public users: any[] = [];
  public teamRoles: TeamRole[] = [];
  public subRoles: Code[] = [];
  public confirmDeleteDialogOpened = false;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Filtered list of team roles for drop-down.
   */
  public teamRolesFiltered: TeamRole[] = [];

  public notifications: Array<{ text: string; value: string }> = [];

  private teamEdit: TeamEdit;

  constructor(
    private fb: UntypedFormBuilder,
    private messageHandlerService: MessageHandlerService,
    private userService: UserService,
    private dictionaryService: DictionaryService,
    private teamService: TeamService,
    private settingService: SettingService)
  { }

  public get user(): UntypedFormControl
  {
    return this.editForm.get('user');
  }

  public get notification(): UntypedFormControl
  {
    return this.editForm.get('notification');
  }

  @Input() public set model(team: TeamEdit)
  {
    this.initModel(team);
  }

  public ngOnInit(): void
  {
    this.getRoles();
    this.notifications = this.teamService.notifications;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = merge(this.teamEdit, this.editForm.value);

    this.save.emit(objectToPersist);
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  public onDelete(e: Event): void
  {
    e.preventDefault();

    this.delete.emit(this.teamEdit);
    this.editFormActive = false;
    document.body.style.overflow = '';
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public subRole(): UntypedFormControl
  {
    return this.editForm.get('subRole');
  }

  /**
   * Filters the team roles on user input.
   */
  public filterTeamRoles(filter: string): void
  {
    this.searchTerm = filter;
    this.teamRolesFiltered = this.teamRoles
      .filter(user => user.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Check if team role from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public roleItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public userDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration && itemArgs.dataItem.username;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    document.body.style.overflow = '';
    this.cancelling.emit();
  }

  private initModel(team: TeamEdit)
  {
    if (team === undefined) { return; }

    this.getUsers();

    this.displaySubRoles = false;
    if (this.isNew)
    {
      // Set form
      this.editForm = this.fb.group({
        id: [null],
        user: [null, Validators.required],
        role: [null, Validators.required],
        member: [null],
        case: [null],
        subRole: [null],
        notification: ['NO_NOTIFICATION', Validators.required]
      });
    }
    else
    {
      const subRoleValidator = [];
      this.displaySubRoles = false;

      if (team.role !== null)
      {
        if (team.role.teamType !== null)
        {
          this.getSubRole(team.role.teamType);
          subRoleValidator.push(Validators.required);
        }
      }

      this.editForm = this.fb.group({
        id: [team.id],
        user: [{ value: team.user, disabled: true }, Validators.required],
        role: [team.role, Validators.required],
        case: [team.case],
        member: [team.member],
        subRole: [{ value: team.subRole, disabled: false }, subRoleValidator],
        notification: [team.notification, Validators.required]
      });

      // Set the user full name
      if (team.user !== null)
      {
        this.userService.getUserById(team.user.id, false).subscribe({
          next: (user) =>
          {
            if (user !== null)
            {
              const selectedUser = { id: user.id, name: user.fullName };
              this.users.push(selectedUser);
              this.user.setValue(selectedUser);
              this.user.updateValueAndValidity();
            }
          }
        });
      }
    }

    // Subscribe on role changes to handle subRole requirement
    this.editForm.get('role').valueChanges.subscribe({
      next: (role: any) =>
      {
        if (role !== undefined && role.teamType !== null)
        {
          this.editForm.get('subRole').setValidators([Validators.required]);
        }
        else
        {
          this.editForm.get('subRole').setValidators([]);
        }
        this.editForm.get('subRole').setValue(null);
        this.editForm.get('subRole').updateValueAndValidity();
      }
    });

    this.teamEdit = team;
    this.editFormActive = true;
    document.body.style.overflow = 'hidden';

    if (this.caseId !== undefined)
    {
      this.editForm.controls.case.setValue(this.caseId);
    }
    else if (this.memberId !== undefined)
    {
      this.editForm.controls.member.setValue(this.memberId);
    }
  }

  private getUsers()
  {
    if (this.caseId !== undefined)
    {
      this.userService.getUsersByNotCase(this.caseId).subscribe({
        next: data =>
        {
          this.users = data;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else if (this.memberId !== undefined)
    {
      this.userService.getUsersByNotMember(this.memberId).subscribe({
        next: (data) =>
        {
          this.users = data;
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  private getRoles()
  {
    this.teamService.getTeamRoles().subscribe({
      next: (data) =>
      {
        this.teamRoles = sortBy(data, ['name']);
        this.filterTeamRoles('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private getSubRole(codeSetId: number)
  {
    if (codeSetId === null || codeSetId === undefined)
    {
      this.displaySubRoles = false;
    }
    else
    {
      this.dictionaryService.getCodesByCodeSetId(codeSetId).subscribe({
        next: (data) =>
        { this.subRoles = data; }
      });
      this.displaySubRoles = true;
    }
  }
}
